import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { canOpenClientPage, clientLink, currentEntry } from "../nav";
import { pageMatches } from "../sidebar-mode";

/**
 * Related pages share one sidebar entry (nav.ts `tabs`); these tabs, above the
 * page, move between them. Each page keeps its own URL and header.
 */
export default function SectionTabs() {
  const { pathname, search } = useLocation();
  const { user } = useAuth();
  const { client } = useClientSelection();
  const strip = useRef<HTMLElement>(null);
  const [more, setMore] = useState(false);
  // Phones: the strip scrolls sideways. The open tab scrolls into the strip (never the page), and while
  // tabs sit past the right edge the strip fades there, so a cut-off "Ad spend" reads as "more".
  useEffect(() => {
    const el = strip.current;
    if (!el) return;
    const measure = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    const active = el.querySelector<HTMLElement>('[aria-current="page"]');
    if (active) {
      const box = el.getBoundingClientRect(), r = active.getBoundingClientRect();
      if (r.left < box.left) el.scrollLeft -= box.left - r.left + 16;
      else if (r.right > box.right) el.scrollLeft += r.right - box.right + 16;
    }
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => { el.removeEventListener("scroll", measure); observer?.disconnect(); };
  }, [pathname, search, client?.id, user?.role]);
  const found = currentEntry(pathname, search);
  const siblings = found?.item.tabs;
  if (!found || !siblings) return null;
  const { item, clientSide } = found;
  if (clientSide && !client) return null;
  const tabs = clientSide
    ? siblings.filter((t) => canOpenClientPage(t, user?.role, client?.service_tier, client?.services_off))
    : siblings;
  if (tabs.length < 2) return null;
  // The Ads range (7d, 30d, custom dates) rides along between sibling tabs, so going back to a report keeps it.
  const params = new URLSearchParams(search);
  const keep = ["days", "from", "to"].filter((k) => params.get(k)).map((k) => `${k}=${encodeURIComponent(params.get(k)!)}`).join("&");
  const withRange = (to: string) => (keep ? `${to}${to.includes("?") ? "&" : "?"}${keep}` : to);
  return (
    <nav ref={strip} className="section-tabs" aria-label={`${item.label} pages`} data-more={more || undefined}>
      {tabs.map((tab) => (
        <Link
          key={`${tab.to}:${tab.view || ""}`}
          to={withRange(clientSide && client ? clientLink(tab, client.id) : tab.view ? `${tab.to}?view=${tab.view}` : tab.to)}
          aria-current={pageMatches(pathname, search, tab, siblings) ? "page" : undefined}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}
